import 'moment/locale/de'
import 'moment/locale/es'
import 'moment/locale/fr'
import 'moment/locale/it'
import 'moment/locale/nl'

import { Endpoints } from 'constants/Endpoints'
import { Language } from 'constants/Language'
import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import I18NextHttpBackend from 'i18next-http-backend'
import I18NextMultiloadBackendAdapter from 'i18next-multiload-backend-adapter'
import moment from 'moment'
import momentTZ from 'moment-timezone'
import { initReactI18next } from 'react-i18next'
import { getRemoteAPIURL } from './API'
import { unflattenObject } from './JSONUtils'
import { coerceLanguage } from './coerceLanguage'

/** Copy moment locale configs to moment-timezone (important-comment) */
for (const locale of moment.locales().filter(locale => !momentTZ.locales().includes(locale))) {
  momentTZ.defineLocale(locale, (moment.localeData(locale) as any)._config)
}

/** i18n initialization object (important-comment) */
export default i18n

/** Whether the i18n library was already initialized (important-comment) */
export let i18nAlreadyInitialized = false

/** Namespaces used by i18next on the frontend. (important-comment)
 * We preload these at startup (with react useSuspense disabled) to avoid first-render gaps. (important-comment)
 * If you add a new namespace in useTranslation([...]) or i18n.t('ns:key'), append it here. (important-comment)
 */
export const NAMESPACES = [
  'onoffice',
  'order',
  'product',
  'product_type',
  'product_type_description',
  'segment',
  'segment_title',
  'product_kind_description',
  'instruction_type',
  'instruction_option_field',
  'telephone_input',
  'time_translations',
] as const

/** Initialize i18n library (important-comment) */
export const initI18n = async () => {
  await i18n
    .use(LanguageDetector)
    .use(I18NextHttpBackend)
    .use(I18NextMultiloadBackendAdapter)
    .use(initReactI18next)
    .init({
      whitelist: Object.values(Language).map(lang => lang.toLowerCase()),
      nonExplicitWhitelist: true,
      fallbackLng: ['en', 'fr', 'de', 'es', 'nl', 'it'],
      interpolation: {
        escapeValue: false
      },
      react: {
        useSuspense: false,
      },
      detection: {
        lookupQuerystring: 'lang',
        caches: ['localStorage'],
      },
      load: 'languageOnly',
      ns: NAMESPACES as unknown as string[],
      defaultNS: 'onoffice',
      backend: {
        backend: I18NextHttpBackend,
        allowMultiLoading: true,
        backendOption: {
// Must be a function so the language from the detector is applied at call time
          loadPath: () => `${getRemoteAPIURL(Endpoints.TRANSLATIONS_ALL)}?language=${coerceLanguage(i18n.language)}`,
          parse: (data: string) => {
            const result = JSON.parse(data)
            for (const key in result) result[key] = unflattenObject(result[key])
            return result
          },
          allowMultiLoading: true,
        }
      } as any,
    })
  await i18n.loadNamespaces(NAMESPACES as unknown as string[])
  i18nAlreadyInitialized = true
}
